/**
 * Extrai mensagem legível de respostas HTTP (Problem Details / middleware Atlas).
 * Ordem: title → message → detail → status HTTP → fallback.
 */
export function extractApiError(
  error: unknown,
  fallback = 'Não foi possível concluir a operação.'
): string {
  const status = getHttpStatus(error);

  if (status === 0 || isNetworkFailure(error)) {
    return (
      'A resposta do cadastro não chegou ao navegador (a API pode ter gravado mesmo assim). ' +
      'Atualize Minha Rota e veja a rota «Cadastros in loco», ou pressione Ctrl+F5 e tente de novo.'
    );
  }

  if (isTimeoutError(error)) {
    return 'A API demorou demais para responder. Tente novamente.';
  }

  if (status === 401) {
    return pickMessage(error) || 'Sessão expirada ou credenciais inválidas. Faça login novamente.';
  }

  if (status === 403) {
    return pickMessage(error) || 'Acesso negado. Seu perfil não tem permissão para esta operação.';
  }

  if (status === 404) {
    return pickMessage(error) || 'Não foi possível encontrar este item.';
  }

  if (status === 409) {
    return pickMessage(error) || 'Conflito ao processar a solicitação. Atualize e tente novamente.';
  }

  if (status === 503) {
    return pickMessage(error)
      || 'Banco de dados indisponível. Conecte a VPN e recarregue a página.';
  }

  if (status != null && status >= 500) {
    return pickMessage(error)
      || 'Ocorreu um erro inesperado. Tente de novo em instantes. Se persistir, avise o suporte.';
  }

  const raw = pickRawMessage(error);
  if (raw?.toLowerCase().includes('transient failure')) {
    return 'Banco de dados indisponível. Conecte a VPN e recarregue a página.';
  }

  return pickMessage(error) || fallback;
}

export function getHttpStatus(error: unknown): number | null {
  if (!error || typeof error !== 'object') {
    return null;
  }

  const status = (error as { status?: unknown }).status;
  return typeof status === 'number' ? status : null;
}

export function isUnauthorized(error: unknown): boolean {
  return getHttpStatus(error) === 401;
}

export function isForbidden(error: unknown): boolean {
  return getHttpStatus(error) === 403;
}

export function isNotFound(error: unknown): boolean {
  return getHttpStatus(error) === 404;
}

function isTimeoutError(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false;
  }

  const err = error as { name?: string; message?: string };
  const name = (err.name || '').toLowerCase();
  const message = (err.message || '').toLowerCase();
  return name === 'timeouterror' || message.includes('timeout');
}

function isNetworkFailure(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false;
  }

  const err = error as { status?: number; message?: string; name?: string };
  if (err.status === 0) {
    return true;
  }

  const message = `${err.message || ''} ${err.name || ''}`.toLowerCase();
  return message.includes('unknown error')
    || message.includes('failed to fetch')
    || message.includes('network')
    || message.includes('progress event');
}

/**
 * Mensagem técnica (cliente HTTP, exceção .NET, stack, erro de banco) nunca vai para a tela.
 */
export function isTechnicalMessage(message: string): boolean {
  return /Http failure (response|during parsing)|Exception\b|\bSystem\.|Npgsql|EntityFramework|Microsoft\.|Object reference|\bat [\w.]+\(|\bstack ?trace|TypeError|SyntaxError|Unexpected token|execution strategy|transaction/i
    .test(message);
}

function pickMessage(error: unknown): string | null {
  const message = pickRawMessage(error);
  return message && !isTechnicalMessage(message) ? message : null;
}

function pickRawMessage(error: unknown): string | null {
  if (error == null) {
    return null;
  }

  if (typeof error === 'string' && error.trim()) {
    return error.trim();
  }

  if (error instanceof Error && error.message.trim() && !isGenericHttpClientMessage(error.message)) {
    return error.message.trim();
  }

  const body = (error as { error?: unknown })?.error ?? error;

  if (typeof body === 'string' && body.trim()) {
    // HTML/IIS pages are useless to the user
    if (body.trimStart().startsWith('<')) {
      return null;
    }
    return body.trim();
  }

  if (body instanceof Error && body.message.trim()) {
    return body.message.trim();
  }

  if (body && typeof body === 'object') {
    const record = body as Record<string, unknown>;
    for (const key of ['title', 'message', 'detail'] as const) {
      const value = record[key];
      if (typeof value === 'string' && value.trim()) {
        return value.trim();
      }
    }
  }

  return null;
}

function isGenericHttpClientMessage(message: string): boolean {
  return /^Http failure response for /i.test(message);
}
